/**
 * Listing capture page (mobile-first).
 *
 * Single-screen form for both create and edit. Required fields surface
 * above the fold on a phone (headline, photos, address, price, BHK/size).
 * Optional fields (description, amenities) live below in a collapsible
 * panel so the form stays scannable.
 *
 * Address-privacy split is the form's core privacy mechanism: the
 * realtor enters the full address once; we auto-derive a public version
 * (without flat / unit number) and let them edit either side. The
 * backend stores both columns; only `address_public` ever reaches a
 * non-owner.
 *
 * Photos use the existing MultiImageUpload — same pipeline as designer
 * portfolios and contractor work-photos.
 */

import {
	ArrowLeft,
	ChevronDown,
	ChevronUp,
	Save,
	ShieldCheck,
} from "lucide-react";
import { cloneElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
	type CreateListingInput,
	createListing,
	getMyListing,
	type Listing,
	type PropertyType,
	updateListing,
} from "@/features/realtor/services/listings-api.service";
import {
	formatPaiseAsRupeeInput,
	formatPaiseAsRupees,
	parseRupeeInputToPaise,
} from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";

const PROPERTY_TYPES: PropertyType[] = ["apartment", "house", "villa", "plot"];
const MAX_IMAGES = 10;

interface FormState {
	headline: string;
	propertyType: PropertyType;
	bhk: string;
	sizeSqft: string;
	addressPrivate: string;
	addressPublic: string;
	city: string;
	locality: string;
	priceInput: string;
	images: string[];
	description: string;
	amenitiesInput: string;
	isExclusive: boolean;
	manualPublicAddress: boolean;
}

const EMPTY_FORM: FormState = {
	headline: "",
	propertyType: "apartment",
	bhk: "",
	sizeSqft: "",
	addressPrivate: "",
	addressPublic: "",
	city: "",
	locality: "",
	priceInput: "",
	images: [],
	description: "",
	amenitiesInput: "",
	isExclusive: false,
	manualPublicAddress: false,
};

export function ListingCapturePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isEdit = Boolean(id);
	const [isLoading, setIsLoading] = useState(isEdit);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<
		Partial<Record<keyof FormState, string>>
	>({});
	const [showOptional, setShowOptional] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!isEdit || !id) return;
		let cancelled = false;
		(async () => {
			const result = await getMyListing(id);
			if (cancelled) return;
			if (!result.success || !result.data) {
				setLoadError(result.error ?? t("realtor.listings.loadFailedFallback"));
				setIsLoading(false);
				return;
			}
			setForm(toFormState(result.data));
			setIsLoading(false);
		})();
		return () => {
			cancelled = true;
		};
	}, [id, isEdit, t]);

	// Auto-derive public address from private address until the realtor
	// chooses to edit the public version manually. Common case: flat 204
	// is stripped, the rest stays. Realtor can override either side.
	const derivedPublicAddress = useMemo(
		() => derivePublicAddress(form.addressPrivate),
		[form.addressPrivate],
	);

	useEffect(() => {
		if (form.manualPublicAddress) return;
		if (derivedPublicAddress === form.addressPublic) return;
		setForm((prev) => ({ ...prev, addressPublic: derivedPublicAddress }));
	}, [derivedPublicAddress, form.addressPublic, form.manualPublicAddress]);

	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const { errors: fieldErrors, payload } = validateForm(form, t);
		setErrors(fieldErrors);
		if (Object.keys(fieldErrors).length > 0) {
			toast.error(t("realtor.listings.form.invalidToast"));
			return;
		}
		setIsSubmitting(true);
		const result =
			isEdit && id
				? await updateListing(id, payload)
				: await createListing(payload);
		setIsSubmitting(false);

		if (!result.success || !result.data) {
			toast.error(
				result.error ?? t("realtor.listings.form.submitErrorFallback"),
			);
			return;
		}

		toast.success(
			isEdit
				? t("realtor.listings.form.updatedToast")
				: t("realtor.listings.form.createdToast"),
		);
		navigate(`/realtor/listings/${result.data.id}`);
	};

	if (isLoading) {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-3"
				data-testid="listing-capture-loading"
			>
				<Skeleton className="h-12 w-full bg-dashboard-bg" />
				<Skeleton className="h-48 w-full bg-dashboard-bg" />
				<Skeleton className="h-48 w-full bg-dashboard-bg" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="p-6 bg-dashboard-surface min-h-screen">
				<button
					type="button"
					onClick={() => navigate("/dashboard")}
					className="flex items-center gap-2 text-sm text-dashboard-muted hover:text-dashboard-text transition-colors mb-4"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to dashboard
				</button>
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<p className="text-sm text-dashboard-muted">{loadError}</p>
						<StudioButton
							variant="secondary"
							onClick={() => navigate("/dashboard")}
						>
							{t("realtor.listings.backToList")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="space-y-6 p-6 bg-dashboard-surface min-h-screen"
			noValidate
		>
			{/* Back button */}
			<button
				type="button"
				onClick={() => navigate("/dashboard")}
				className="flex items-center gap-2 text-sm text-dashboard-muted hover:text-dashboard-text transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to dashboard
			</button>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-1">
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						{isEdit ? "EDIT LISTING" : "NEW LISTING"}
					</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 32,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
					>
						{isEdit ? (
							<>Edit your listing</>
						) : (
							<>
								Add a{" "}
								<span
									style={{ color: "var(--color-dashboard-accent-display)" }}
								>
									new
								</span>{" "}
								property
							</>
						)}
					</h1>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							color: "var(--color-dashboard-muted)",
							marginTop: 8,
						}}
					>
						{t("realtor.listings.form.subtitle")}
					</p>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-5">
					<FormField
						id="headline"
						label={t("realtor.listings.form.headlineLabel")}
						error={errors.headline}
					>
						<Input
							id="headline"
							value={form.headline}
							onChange={(e) => setField("headline", e.target.value)}
							placeholder={t("realtor.listings.form.headlinePlaceholder")}
							maxLength={140}
							aria-invalid={errors.headline ? true : undefined}
						/>
					</FormField>

					<div className="grid grid-cols-2 gap-3">
						<FormField
							id="propertyType"
							label={t("realtor.listings.form.propertyTypeLabel")}
							error={errors.propertyType}
						>
							<Select
								value={form.propertyType}
								onValueChange={(value) =>
									setField("propertyType", value as PropertyType)
								}
							>
								<SelectTrigger id="propertyType">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{PROPERTY_TYPES.map((type) => (
										<SelectItem key={type} value={type}>
											{t(`realtor.listings.propertyType.${type}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>

						<FormField
							id="bhk"
							label={t("realtor.listings.form.bhkLabel")}
							error={errors.bhk}
							hint={
								form.propertyType === "plot"
									? t("realtor.listings.form.bhkPlotHint")
									: undefined
							}
						>
							<Input
								id="bhk"
								type="number"
								inputMode="numeric"
								value={form.bhk}
								disabled={form.propertyType === "plot"}
								onChange={(e) => setField("bhk", e.target.value)}
								placeholder="3"
								aria-invalid={errors.bhk ? true : undefined}
							/>
						</FormField>
					</div>

					<FormField
						id="sizeSqft"
						label={t("realtor.listings.form.sizeLabel")}
						error={errors.sizeSqft}
					>
						<Input
							id="sizeSqft"
							type="number"
							inputMode="numeric"
							value={form.sizeSqft}
							onChange={(e) => setField("sizeSqft", e.target.value)}
							placeholder="1200"
							aria-invalid={errors.sizeSqft ? true : undefined}
						/>
					</FormField>

					<FormField
						id="priceInput"
						label={t("realtor.listings.form.priceLabel")}
						error={errors.priceInput}
						hint={
							previewPriceLabel(form.priceInput) ??
							t("realtor.listings.form.priceHint")
						}
					>
						<Input
							id="priceInput"
							value={form.priceInput}
							onChange={(e) => setField("priceInput", e.target.value)}
							placeholder={t("realtor.listings.form.pricePlaceholder")}
							aria-invalid={errors.priceInput ? true : undefined}
						/>
					</FormField>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-5">
					<FormField
						id="addressPrivate"
						label={t("realtor.listings.form.addressPrivateLabel")}
						error={errors.addressPrivate}
						hint={t("realtor.listings.form.addressPrivateHint")}
					>
						<Input
							id="addressPrivate"
							value={form.addressPrivate}
							onChange={(e) => setField("addressPrivate", e.target.value)}
							placeholder={t("realtor.listings.form.addressPrivatePlaceholder")}
							aria-invalid={errors.addressPrivate ? true : undefined}
						/>
					</FormField>

					<FormField
						id="addressPublic"
						label={t("realtor.listings.form.addressPublicLabel")}
						error={errors.addressPublic}
						hint={t("realtor.listings.form.addressPublicHint")}
					>
						<Input
							id="addressPublic"
							value={form.addressPublic}
							onChange={(e) => {
								setField("addressPublic", e.target.value);
								// Once the realtor edits the public address manually,
								// stop overwriting it from the private one.
								if (!form.manualPublicAddress) {
									setField("manualPublicAddress", true);
								}
							}}
							placeholder={t("realtor.listings.form.addressPublicPlaceholder")}
							aria-invalid={errors.addressPublic ? true : undefined}
						/>
					</FormField>

					<div className="grid grid-cols-2 gap-3">
						<FormField
							id="city"
							label={t("realtor.listings.form.cityLabel")}
							error={errors.city}
						>
							<Input
								id="city"
								value={form.city}
								onChange={(e) => setField("city", e.target.value)}
								placeholder="Gurgaon"
								aria-invalid={errors.city ? true : undefined}
							/>
						</FormField>
						<FormField
							id="locality"
							label={t("realtor.listings.form.localityLabel")}
							error={errors.locality}
						>
							<Input
								id="locality"
								value={form.locality}
								onChange={(e) => setField("locality", e.target.value)}
								placeholder="Sector 56"
								aria-invalid={errors.locality ? true : undefined}
							/>
						</FormField>
					</div>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-3">
					<Label className="text-xs font-medium uppercase tracking-wide text-dashboard-muted">
						{t("realtor.listings.form.photosLabel", {
							count: form.images.length,
							max: MAX_IMAGES,
						})}
					</Label>
					<MultiImageUpload
						currentUrls={form.images}
						maxImages={MAX_IMAGES}
						onUpload={(urls) =>
							setField("images", [...form.images, ...urls].slice(0, MAX_IMAGES))
						}
						onRemove={(index) =>
							setField(
								"images",
								form.images.filter((_, i) => i !== index),
							)
						}
						label={t("realtor.listings.form.photoUploadLabel")}
					/>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-1">
					<button
						type="button"
						className="flex w-full items-center justify-between text-sm font-medium text-dashboard-text"
						onClick={() => setShowOptional((v) => !v)}
					>
						<span>{t("realtor.listings.form.optionalSectionLabel")}</span>
						{showOptional ? (
							<ChevronUp className="h-4 w-4 text-dashboard-muted" />
						) : (
							<ChevronDown className="h-4 w-4 text-dashboard-muted" />
						)}
					</button>
					{showOptional && (
						<div className="space-y-4 pt-3">
							<FormField
								id="description"
								label={t("realtor.listings.form.descriptionLabel")}
								error={errors.description}
							>
								<Textarea
									id="description"
									value={form.description}
									onChange={(e) => setField("description", e.target.value)}
									rows={4}
									maxLength={5000}
								/>
							</FormField>
							<FormField
								id="amenitiesInput"
								label={t("realtor.listings.form.amenitiesLabel")}
								error={errors.amenitiesInput}
								hint={t("realtor.listings.form.amenitiesHint")}
							>
								<Input
									id="amenitiesInput"
									value={form.amenitiesInput}
									onChange={(e) => setField("amenitiesInput", e.target.value)}
									placeholder="Pool, Gym, Parking"
								/>
							</FormField>
							<div className="flex items-center justify-between">
								<div className="space-y-0.5">
									<Label
										htmlFor="isExclusive"
										className="text-sm text-dashboard-text"
									>
										{t("realtor.listings.form.exclusiveLabel")}
									</Label>
									<p className="text-xs text-dashboard-muted">
										{t("realtor.listings.form.exclusiveHint")}
									</p>
								</div>
								<Switch
									id="isExclusive"
									checked={form.isExclusive}
									onCheckedChange={(checked) =>
										setField("isExclusive", checked)
									}
								/>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<Card className="border-dashboard-warning-border bg-dashboard-warm-050 rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-4 flex items-start gap-2">
					<ShieldCheck className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
					<p className="text-xs text-dashboard-warning">
						{t("realtor.listings.form.draftNotice")}
					</p>
				</CardContent>
			</Card>

			<div className="flex justify-end gap-2">
				<StudioButton
					variant="secondary"
					onClick={() => navigate("/dashboard")}
				>
					{t("realtor.listings.form.cancel")}
				</StudioButton>
				<StudioButton type="submit" disabled={isSubmitting}>
					<Save className="h-4 w-4" />
					{isSubmitting
						? t("realtor.listings.form.submitting")
						: isEdit
							? t("realtor.listings.form.saveChanges")
							: t("realtor.listings.form.createDraft")}
				</StudioButton>
			</div>
		</form>
	);
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

interface FormFieldProps {
	id: string;
	label: string;
	error?: string;
	hint?: string;
	children: React.ReactNode;
}

function FormField({ id, label, error, hint, children }: FormFieldProps) {
	const errorId = error ? `${id}-error` : undefined;
	const hintId = hint && !error ? `${id}-hint` : undefined;
	const describedBy = errorId ?? hintId;
	// Push aria-describedby + aria-invalid down onto the single input child
	// so screen readers announce the error/hint when the field gets focus.
	// Per-input aria-invalid in the JSX above is now redundant but harmless;
	// this wrapper is the seam.
	const child = isValidSingleElement(children)
		? cloneElement(children, {
				"aria-invalid": error ? true : undefined,
				"aria-describedby": describedBy,
			} as Record<string, unknown>)
		: children;
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id} className="text-dashboard-text">
				{label}
			</Label>
			{child}
			{error && (
				<p
					id={errorId}
					className="text-xs text-dashboard-pill-risk"
					role="alert"
				>
					{error}
				</p>
			)}
			{!error && hint && (
				<p id={hintId} className="text-xs text-dashboard-muted">
					{hint}
				</p>
			)}
		</div>
	);
}

function isValidSingleElement(
	node: React.ReactNode,
): node is React.ReactElement {
	return Boolean(node) && typeof node === "object" && !Array.isArray(node);
}

function toFormState(listing: Listing): FormState {
	return {
		headline: listing.headline,
		propertyType: listing.propertyType,
		bhk: listing.bhk?.toString() ?? "",
		sizeSqft: listing.sizeSqft?.toString() ?? "",
		addressPrivate: listing.addressPrivate,
		addressPublic: listing.addressPublic,
		city: listing.city,
		locality: listing.locality,
		// Re-format paise back into the lakh / crore shorthand the realtor
		// most likely typed originally. Falling back to plain rupees if
		// the value is below the lakh threshold.
		priceInput: formatPaiseAsRupeeInput(listing.askingPrice),
		images: listing.images,
		description: listing.description ?? "",
		amenitiesInput: listing.amenities.join(", "),
		// In edit mode the public address has already been saved and
		// shouldn't be re-derived from the private one — the realtor
		// already curated it.
		isExclusive: listing.isExclusive,
		manualPublicAddress: true,
	};
}

/**
 * Strip a leading flat / unit / house number off a private address. The
 * auto-derivation is a HINT — the realtor can override the public field.
 * Even so, leaking a flat number into the public address is the privacy
 * failure this whole feature exists to prevent, so the regex should err
 * on the side of stripping.
 *
 * Patterns covered:
 *   - "C-204," / "B-204" / "B 204"  — letter + (hyphen | space) + number
 *   - "Flat 12," / "Apt 4B," / "House 7,"
 *   - "H No 5,"  — "H No" abbreviation common in N. India
 *   - "#204,"  — hash prefix common in Bangalore
 *   - "Plot 12," / "Plot No 12,"
 *   - "204,"  — bare leading number, very common
 *
 * If the realtor wants the flat number kept (some Indian listings do),
 * they edit the public field manually — `manualPublicAddress` then
 * pins the value.
 */
function derivePublicAddress(privateAddress: string): string {
	const trimmed = privateAddress.trim();
	if (!trimmed) return "";
	const stripped = trimmed.replace(
		/^(?:#\s*\d+[a-z]?(?:\/[a-z0-9]+)?|plot\s*(?:no\.?\s*)?\d+[a-z]?|flat\s*(?:no\.?\s*)?\d+[a-z]?|apt\s*(?:no\.?\s*)?\d+[a-z]?|house\s*(?:no\.?\s*)?\d+[a-z]?|h\.?\s*no\.?\s*\d+[a-z]?|[a-z]\s*-?\s*\d+[a-z]?(?:\/[a-z0-9]+)?|\d+[a-z]?(?:\/[a-z0-9]+)?)\s*[,\s]+/i,
		"",
	);
	return stripped;
}

function previewPriceLabel(input: string): string | null {
	const paise = parseRupeeInputToPaise(input);
	if (paise == null) return null;
	return formatPaiseAsRupees(paise);
}

interface ValidatedForm {
	errors: Partial<Record<keyof FormState, string>>;
	payload: CreateListingInput;
}

function validateForm(
	form: FormState,
	t: (key: string, options?: Record<string, unknown>) => string,
): ValidatedForm {
	const errors: Partial<Record<keyof FormState, string>> = {};

	const headline = form.headline.trim();
	if (!headline) errors.headline = t("realtor.listings.form.errors.headline");
	else if (headline.length > 140)
		errors.headline = t("realtor.listings.form.errors.headlineLong");

	if (!form.addressPrivate.trim())
		errors.addressPrivate = t("realtor.listings.form.errors.addressPrivate");
	if (!form.addressPublic.trim())
		errors.addressPublic = t("realtor.listings.form.errors.addressPublic");
	if (!form.city.trim()) errors.city = t("realtor.listings.form.errors.city");
	if (!form.locality.trim())
		errors.locality = t("realtor.listings.form.errors.locality");

	let bhkValue: number | null = null;
	if (form.bhk.trim()) {
		const parsed = Number.parseInt(form.bhk, 10);
		if (!Number.isInteger(parsed) || parsed < 0 || parsed > 20) {
			errors.bhk = t("realtor.listings.form.errors.bhk");
		} else {
			bhkValue = parsed;
		}
	}

	if (form.propertyType === "plot" && bhkValue != null) {
		errors.bhk = t("realtor.listings.form.errors.bhkPlot");
	}

	let sizeValue: number | null = null;
	if (form.sizeSqft.trim()) {
		const parsed = Number.parseInt(form.sizeSqft, 10);
		if (!Number.isInteger(parsed) || parsed <= 0) {
			errors.sizeSqft = t("realtor.listings.form.errors.size");
		} else {
			sizeValue = parsed;
		}
	}

	const paise = parseRupeeInputToPaise(form.priceInput);
	if (paise == null) {
		errors.priceInput = t("realtor.listings.form.errors.price");
	}

	const amenities = form.amenitiesInput
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
	if (amenities.length > 30) {
		errors.amenitiesInput = t("realtor.listings.form.errors.amenitiesCount");
	}

	const payload: CreateListingInput = {
		propertyType: form.propertyType,
		bhk: form.propertyType === "plot" ? null : bhkValue,
		sizeSqft: sizeValue,
		addressPublic: form.addressPublic.trim(),
		addressPrivate: form.addressPrivate.trim(),
		city: form.city.trim(),
		locality: form.locality.trim(),
		askingPrice: paise ?? 0,
		headline,
		description: form.description.trim() || null,
		images: form.images,
		amenities,
		isExclusive: form.isExclusive,
	};

	return { errors, payload };
}
